import { supabase } from '../lib/supabase';
import {
  OrgPermissions,
  ALL_PERMISSIONS,
  EMPLOYEE_PERMISSIONS,
} from '../types/permissions';

export type { OrgPermissions };

export interface Organization {
  id: string;
  name: string;
  slug: string;
  created_at: string;
}

export interface OrgRole {
  id: string;
  org_id: string;
  name: string;
  description: string;
  level: 'owner' | 'admin' | 'member';
  is_system: boolean;
  permissions: OrgPermissions;
  created_at: string;
  member_count?: number;
}

export interface OrgMember {
  id: string;
  org_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  role_id: string;
  role_name?: string;
  role_level?: 'owner' | 'admin' | 'member';
  joined_at: string;
  is_active: boolean;
  full_name?: string;
  email?: string;
}

export interface OrgInvitation {
  id: string;
  org_id: string;
  email: string;
  token: string;
  role: 'admin' | 'member';
  role_id?: string;
  role_name?: string;
  invited_by: string;
  status: 'pending' | 'accepted' | 'revoked';
  created_at: string;
  expires_at: string;
  metadata?: {
    name?: string;
    designation?: string;
    phone?: string;
    address?: string;
  };
}

// ─── Helpers ─────────────────────────────────────────────────

function slugify(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);
}

function uniqueSlug(base: string): string {
  return `${base}-${Math.random().toString(36).slice(2, 7)}`;
}

// ─── Service ─────────────────────────────────────────────────

export const organizationService = {

  // ── ORG ────────────────────────────────────────────────────

  async createOrganization(name: string): Promise<Organization> {
    const slug = uniqueSlug(slugify(name));
    const { data, error } = await supabase
      .from('organizations')
      .insert({ name, slug })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getMyOrganization(): Promise<Organization | null> {
    const { data, error } = await supabase.from('organizations').select('*').single();
    if (error) return null;
    return data;
  },

  // ── ROLES ───────────────────────────────────────────────────

  // Called right after org creation — inserts Owner, Admin, Employee system roles.
  // At this point no members exist yet, so the "creator_can_seed_default_roles" RLS policy passes.
  async createDefaultRoles(orgId: string): Promise<{ ownerId: string; adminId: string; employeeId: string }> {
    const { data, error } = await supabase
      .from('org_roles')
      .insert([
        {
          org_id: orgId,
          name: 'Owner',
          description: 'Full control over the organization. Assigned to the account creator.',
          level: 'owner',
          is_system: true,
          permissions: ALL_PERMISSIONS,
        },
        {
          org_id: orgId,
          name: 'Admin',
          description: 'Full access to all features and can manage team members.',
          level: 'admin',
          is_system: true,
          permissions: ALL_PERMISSIONS,
        },
        {
          org_id: orgId,
          name: 'Employee',
          description: 'Access to core features. Cannot view or manage team members.',
          level: 'member',
          is_system: true,
          permissions: EMPLOYEE_PERMISSIONS,
        },
      ])
      .select('id, name');

    if (error) throw error;

    const roles = data as { id: string; name: string }[];
    return {
      ownerId:    roles.find(r => r.name === 'Owner')!.id,
      adminId:    roles.find(r => r.name === 'Admin')!.id,
      employeeId: roles.find(r => r.name === 'Employee')!.id,
    };
  },

  async getRoles(orgId: string): Promise<OrgRole[]> {
    const { data, error } = await supabase
      .from('org_roles')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: true });
    if (error) throw error;

    // Count members per role
    const { data: members } = await supabase
      .from('organization_members')
      .select('role_id')
      .eq('org_id', orgId);

    const counts: Record<string, number> = {};
    (members || []).forEach(m => { counts[m.role_id] = (counts[m.role_id] ?? 0) + 1; });

    return (data || []).map(r => ({ ...r, member_count: counts[r.id] ?? 0 }));
  },

  async createRole(
    orgId: string,
    name: string,
    description: string,
    level: 'admin' | 'member',
    permissions: OrgPermissions
  ): Promise<OrgRole> {
    const { data, error } = await supabase
      .from('org_roles')
      .insert({ org_id: orgId, name, description, level, is_system: false, permissions })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateRole(
    roleId: string,
    updates: Partial<Pick<OrgRole, 'name' | 'description' | 'permissions'>>
  ): Promise<void> {
    const { error } = await supabase.from('org_roles').update(updates).eq('id', roleId);
    if (error) throw error;
  },

  async deleteRole(roleId: string): Promise<void> {
    const { error } = await supabase.from('org_roles').delete().eq('id', roleId);
    if (error) throw error;
  },

  // ── MEMBERS ────────────────────────────────────────────────

  async addMember(
    orgId: string,
    userId: string,
    roleId: string,
    level: 'owner' | 'admin' | 'member'
  ): Promise<void> {
    const { error } = await supabase
      .from('organization_members')
      .insert({ org_id: orgId, user_id: userId, role: level, role_id: roleId });
    if (error) throw error;
  },

  async getMembersWithProfiles(orgId: string): Promise<OrgMember[]> {
    // Use RPC that reads auth.users directly — guaranteed name+email
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_org_members_with_emails', { p_org_id: orgId });

    if (!rpcError && rpcData) {
      return (rpcData as any[]).map((m) => ({
        id:         m.id,
        org_id:     m.org_id,
        user_id:    m.user_id,
        role:       m.role,
        role_id:    m.role_id,
        role_name:  m.role_name,
        role_level: m.role_level,
        joined_at:  m.joined_at,
        is_active:  m.is_active ?? true,
        full_name:  m.full_name || '',
        email:      m.email || '',
      }));
    }

    // Fallback: join query if RPC not yet deployed
    const { data: { user: authUser } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('organization_members')
      .select(`*, org_roles:role_id (id, name, level), user_profiles!user_id (full_name, email)`)
      .eq('org_id', orgId)
      .order('joined_at', { ascending: true });

    let rows: any[] = [];
    if (error) {
      const { data: fallback, error: fallbackErr } = await supabase
        .from('organization_members')
        .select(`*, org_roles:role_id (id, name, level)`)
        .eq('org_id', orgId)
        .order('joined_at', { ascending: true });
      if (fallbackErr) throw fallbackErr;
      rows = fallback || [];
    } else {
      rows = data || [];
    }

    return rows.map((m: any) => {
      const profile = m.user_profiles;
      const isCurrentUser = m.user_id === authUser?.id;
      return {
        id:         m.id,
        org_id:     m.org_id,
        user_id:    m.user_id,
        role:       m.role,
        role_id:    m.role_id,
        role_name:  m.org_roles?.name,
        role_level: m.org_roles?.level,
        joined_at:  m.joined_at,
        is_active:  m.is_active ?? true,
        full_name:  profile?.full_name || (isCurrentUser ? (authUser?.user_metadata?.full_name ?? '') : ''),
        email:      profile?.email     || (isCurrentUser ? (authUser?.email ?? '') : ''),
      };
    });
  },

  async toggleMemberActive(orgId: string, userId: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('organization_members')
      .update({ is_active: isActive })
      .eq('org_id', orgId)
      .eq('user_id', userId);
    if (error) throw error;
  },

  async updateMemberRole(
    orgId: string,
    userId: string,
    roleId: string,
    level: 'admin' | 'member'
  ): Promise<void> {
    const { error } = await supabase
      .from('organization_members')
      .update({ role: level, role_id: roleId })
      .eq('org_id', orgId)
      .eq('user_id', userId);
    if (error) throw error;
  },

  async removeMember(orgId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('organization_members')
      .delete()
      .eq('org_id', orgId)
      .eq('user_id', userId);
    if (error) throw error;
  },

  async getMyMembership(): Promise<{
    org_id: string;
    role: 'owner' | 'admin' | 'member';
    role_id: string;
    permissions: OrgPermissions;
  } | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('organization_members')
      .select(`org_id, role, role_id, org_roles:role_id (permissions)`)
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('joined_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;

    const permissions: OrgPermissions =
      (data as any).org_roles?.permissions ?? ALL_PERMISSIONS;

    return {
      org_id:      data.org_id,
      role:        data.role as 'owner' | 'admin' | 'member',
      role_id:     data.role_id,
      permissions,
    };
  },

  // ── INVITATIONS ────────────────────────────────────────────

  async createInvitation(
    orgId: string,
    email: string,
    roleId: string,
    roleName: string,
    orgName?: string,
    metadata?: { name?: string; designation?: string; phone?: string; address?: string }
  ): Promise<OrgInvitation> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Resolve level from role
    const { data: roleData } = await supabase
      .from('org_roles')
      .select('level')
      .eq('id', roleId)
      .single();

    const level = roleData?.level ?? 'member';

    const basePayload = { org_id: orgId, email, role: level, role_id: roleId, role_name: roleName, invited_by: user.id };

    let invitation: OrgInvitation;

    // Try with metadata first; fall back if column doesn't exist yet (migration not run)
    if (metadata) {
      const { data: withMeta, error: metaErr } = await supabase
        .from('organization_invitations')
        .insert({ ...basePayload, metadata })
        .select()
        .single();
      if (!metaErr) {
        invitation = { ...withMeta, role_name: roleName };
      } else {
        const { data, error } = await supabase
          .from('organization_invitations')
          .insert(basePayload)
          .select()
          .single();
        if (error) throw error;
        invitation = { ...data, role_name: roleName };
      }
    } else {
      const { data, error } = await supabase
        .from('organization_invitations')
        .insert(basePayload)
        .select()
        .single();
      if (error) throw error;
      invitation = { ...data, role_name: roleName };
    }

    // Send invitation email via Supabase Edge Function (non-fatal if it fails)
    try {
      await supabase.functions.invoke('send-invite-email', {
        body: {
          email,
          name:    metadata?.name,
          orgName: orgName ?? '',
          role:    roleName,
          token:   invitation.token,
        },
      });
    } catch {
      // Email failure is non-fatal — invitation still created in DB
    }

    return invitation;
  },

  async getPendingInvitations(orgId: string): Promise<OrgInvitation[]> {
    const { data, error } = await supabase
      .from('organization_invitations')
      .select(`*, org_roles:role_id (name)`)
      .eq('org_id', orgId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (error) throw error;

    return (data || []).map((inv: any) => ({
      ...inv,
      role_name: inv.org_roles?.name,
    }));
  },

  async revokeInvitation(invitationId: string): Promise<void> {
    const { error } = await supabase
      .from('organization_invitations')
      .update({ status: 'revoked' })
      .eq('id', invitationId);
    if (error) throw error;
  },

  async getInvitationByToken(token: string): Promise<OrgInvitation | null> {
    const { data, error } = await supabase
      .from('organization_invitations')
      .select('*')
      .eq('token', token)
      .single();
    if (error) return null;
    return data;
  },

  async acceptInvitation(token: string): Promise<void> {
    const { error } = await supabase.rpc('accept_org_invitation', { p_token: token });
    if (error) throw error;
  },
};
